/**
 * localStorage stash used by ComposeEmailModal for dismiss-time auto-save
 * (#2400). Lives outside the component so the recovery + TTL logic can be
 * unit-tested without rendering a portal-bound modal and mocking five
 * Gmail/CRM endpoints.
 *
 * Stash lifecycle:
 *   1. User types in compose modal, then dismisses via X / backdrop /
 *      Escape / Cancel. handleDismiss calls writeDraftStash(...) BEFORE
 *      firing the fire-and-forget POST so a 4xx / network failure can't
 *      silently swallow the work.
 *   2. POST returns 2xx → clearDraftStash() removes the local copy.
 *      Non-2xx or network failure → stash persists.
 *   3. Next modal open for the same workspace+contact reads the stash and
 *      pre-fills the editor.
 *
 * #2715 TTL guard: a draft can sit indefinitely in localStorage until one
 * of the clear paths fires. Pasted credentials / financial data deserve a
 * hard expiry so the stash discards itself after DRAFT_STASH_TTL_MS.
 */

/** Maximum age of a stashed draft before recovery discards it. */
export const DRAFT_STASH_TTL_MS = 24 * 60 * 60 * 1000;

/** Shape persisted in localStorage. `savedAt` is ISO-8601. */
export interface DraftStashPayload {
  to: string;
  subject: string;
  body: string;
  attachmentNames: string[];
}

interface PersistedDraftStash extends DraftStashPayload {
  savedAt: string;
}

/**
 * Build the per-workspace+contact stash key. Returns null when the contact
 * has no stable identifier — in that case we skip the stash entirely rather
 * than fall back to a shared 'anon' bucket where multiple anonymous contacts
 * would collide.
 */
export function buildDraftStashKey(
  contact: { id?: string; email?: string },
  workspaceId: string,
): string | null {
  const contactKey = contact.id || contact.email;
  if (!contactKey) return null;
  return `xavior:compose:draft:${workspaceId}:${contactKey}`;
}

/**
 * Read and validate a stashed draft.
 *
 * Returns null and removes the stash when the payload is stale, malformed,
 * or missing required fields. Returns the recovered draft otherwise.
 * Caller passes `now` for deterministic tests; defaults to `Date.now()`.
 */
export function readDraftStash(
  stashKey: string | null,
  now: number = Date.now(),
): DraftStashPayload | null {
  if (!stashKey) return null;

  let raw: string | null;
  try {
    raw = localStorage.getItem(stashKey);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isValidPayload(parsed)) return null;

  // TTL guard (#2715): discard stashes older than DRAFT_STASH_TTL_MS.
  // Stashes WITH a non-parseable `savedAt` are corrupt — discard, since we
  // can't prove freshness. Stashes WITHOUT `savedAt` are not produced by any
  // current writer (`writeDraftStash` always sets it), but we honor them
  // unconditionally as defense-in-depth for hand-crafted localStorage or a
  // future writer that omits the field.
  if (typeof parsed.savedAt === 'string') {
    const savedAtMs = Date.parse(parsed.savedAt);
    if (Number.isNaN(savedAtMs)) {
      try { localStorage.removeItem(stashKey); } catch { /* best effort */ }
      return null;
    }
    if (now - savedAtMs > DRAFT_STASH_TTL_MS) {
      try { localStorage.removeItem(stashKey); } catch { /* best effort */ }
      return null;
    }
  }

  return {
    to: parsed.to,
    subject: parsed.subject,
    body: parsed.body,
    attachmentNames: parsed.attachmentNames,
  };
}

/**
 * Write the stash with a current-time savedAt. Best-effort: localStorage
 * may throw on QuotaExceededError or when storage is disabled.
 */
export function writeDraftStash(
  stashKey: string | null,
  payload: DraftStashPayload,
  now: number = Date.now(),
): void {
  if (!stashKey) return;
  const persisted: PersistedDraftStash = {
    ...payload,
    savedAt: new Date(now).toISOString(),
  };
  try {
    localStorage.setItem(stashKey, JSON.stringify(persisted));
  } catch (err) {
    console.warn('[composeDraftStash] write failed:', err);
  }
}

/** Best-effort removal — never throws. */
export function clearDraftStash(stashKey: string | null): void {
  if (!stashKey) return;
  try {
    localStorage.removeItem(stashKey);
  } catch {
    // best effort
  }
}

function isValidPayload(value: unknown): value is PersistedDraftStash {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.body === 'string'
    && v.body.trim().length > 0
    && typeof v.to === 'string'
    && typeof v.subject === 'string'
    && Array.isArray(v.attachmentNames)
    // Tighten: every element must be a string. Without this, a corrupted
    // stash like `attachmentNames: [42, {x:1}]` would round-trip through
    // recovery and be re-written on the next dismiss.
    && v.attachmentNames.every((n): n is string => typeof n === 'string')
  );
}
